import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Radar } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { IntelligenceBrief } from "./IntelligenceBrief";
import brand from "@/config/brand";
import { httpGetPublicStats } from "@/services/httpAdapter";

/**
 * Executive Hero — Fursa Design.
 *
 * Positioning: institutional Investment Intelligence platform.
 * Sprint 28a — reduced headline size (~4 typography steps above body),
 * and proof-strip values now hydrated from GET /api/stats/public
 * (honest small numbers, "منذ الإطلاق" hint when appropriate).
 */

const PROOF_ORDER = [
  { key: "signals",       icon: Radar,      label: "إشارة سوق مُحلَّلة", hint: "استكشف الأرشيف",   href: "/signals" },
  { key: "opportunities", icon: Sparkles,   label: "فرصة موثّقة بأدلّة", hint: "منشورة",          href: "/opportunities" },
  { key: "countries",     icon: TrendingUp, label: "دولة تحت التغطية",   hint: "المنطقة العربية", href: null },
];

const usePublicStats = () => {
  const [stats, setStats] = useState({ signals: 0, opportunities: 0, countries: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    httpGetPublicStats()
      .then((s) => {
        if (cancelled) return;
        setStats({
          signals:       Number(s?.signals ?? 0),
          opportunities: Number(s?.opportunities ?? 0),
          countries:     Number(s?.countries ?? 0),
        });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);
  return { stats, loading };
};

export const Hero = () => {
  const { stats, loading } = usePublicStats();

  return (
    <section
      data-testid={HERO.root}
      className="relative overflow-hidden bg-surface-50"
    >
      {/* Ambient dot grid — the only background texture. No gradients. */}
      <div className="pointer-events-none absolute inset-0 fursa-grid-bg opacity-70" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-3 sm:px-6 sm:pb-12 sm:pt-4 lg:px-8 lg:pb-14 lg:pt-6">
        <IntelligenceBrief
          className="hidden lg:block absolute left-4 top-6 max-w-[220px] sm:left-6 lg:left-8 lg:top-8"
        />
        <IntelligenceBriefMobile />

        <div
          data-testid={HERO.eyebrow}
          className="inline-flex items-center gap-2 rounded-full border border-surface-200 bg-white/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-intel-800 shadow-card backdrop-blur-sm"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fursa-500 opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-fursa-600" />
          </span>
          {brand.hero.eyebrow} · {brand.hero.eyebrowAr}
        </div>

        {/* Headline — Sprint 34: strengthened weight + tightened tracking.
            Emphasis rendered on the phrase "فرصة استثمارية" to match the
            new positioning statement. */}
        <h1
          data-testid={HERO.title}
          className="arabic-headline fursa-hero-title mt-4 max-w-[900px] font-extrabold tracking-[-0.01em] text-ink-900 text-balance sm:mt-5"
        >
          {brand.hero.headline.split("فرصة استثمارية")[0]}
          <span className="text-fursa-700">فرصة استثمارية</span>
        </h1>

        <p
          data-testid={HERO.subtitle}
          className="arabic-body fursa-body-lead mt-4 max-w-[640px] text-ink-500 text-pretty sm:mt-5"
        >
          {brand.hero.support}
        </p>

        {/* Proof strip — real backend values via /api/stats/public. */}
        <div
          data-testid="hero-proof-strip"
          className="mt-7 grid grid-cols-3 divide-x divide-x-reverse divide-surface-200 rounded-xl border border-surface-200 bg-white shadow-card sm:mt-9 sm:max-w-3xl"
        >
          {PROOF_ORDER.map(({ key, icon: Icon, label, hint, href }) => {
            const Wrapper = href ? Link : "div";
            const wrapperProps = href
              ? { to: href, className: "block px-3 py-4 transition-colors hover:bg-fursa-50/40 sm:px-6 sm:py-5" }
              : { className: "px-3 py-4 sm:px-6 sm:py-5" };
            return (
              <Wrapper key={key} {...wrapperProps} data-testid={`hero-proof-${key}`}>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-ink-500 sm:text-[11px]">
                  <Icon className="h-3 w-3 text-intel-700 sm:h-3.5 sm:w-3.5" strokeWidth={2} />
                  <span className="truncate">{label}</span>
                </div>
                {loading ? (
                  <div
                    data-testid={`hero-proof-${key}-skeleton`}
                    aria-busy="true"
                    aria-label="جاري التحميل"
                    className="mt-2 h-8 w-16 animate-pulse rounded-md bg-surface-100 sm:h-10 sm:w-24"
                  />
                ) : (
                  <div
                    dir="ltr"
                    data-testid={`hero-proof-${key}-value`}
                    className="num mt-2 font-mono text-[26px] font-bold leading-none tabular-nums text-ink-900 sm:text-[34px]"
                  >
                    {stats[key].toLocaleString("en-US")}
                  </div>
                )}
                <div className="mt-1.5 hidden text-[11px] font-medium text-ink-500 sm:block">
                  {hint}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const IntelligenceBriefMobile = () => (
  <IntelligenceBrief compact className="lg:hidden mb-4" />
);
